var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Import dependencies
import { html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import apiFetch from '@wordpress/api-fetch';
// Import global utils
import { createSnackbar } from '@automator/shared/utils/create-snackbar';
import { throwConfetti } from '@automator/shared/animations/throw-confetti';
// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';
// Import i18n functions
import { __, _x } from '@wordpress/i18n';
// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use(apiFetch.createNonceMiddleware(UncannyAutomatorBackend.rest.nonce));
/**
 * A LitElement web component that renders an action button for a specific Uncanny Automator addon item.
 * It handles API calls for actions like installing, activating, or updating the addon plugin, manages loading states, and displays user feedback via snackbars or dialogs.
 * Expects `addon-id` and `cta` properties to be provided.
 *
 * @fires automator-page-addons-group-item-data-changed - Dispatched when an action successfully completes. Event detail contains `{ addon: Addon }`.
 */
let AutomatorPageAddonsGroupItemButtonAction = class AutomatorPageAddonsGroupItemButtonAction extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The unique identifier (slug or ID) for the addon this button acts upon.
         * Passed via the 'addon-id' attribute.
         */
        this.addonID = '';
        /**
         * Configuration object defining the button's appearance and behavior.
         * Passed via the 'cta' property.
         */
        this.cta = null;
        /**
         * State indicating if an asynchronous action (e.g., install, activate) triggered by this button is currently in progress. Controls the button's loading and disabled appearance. Managed internally.
         */
        this.loading = false;
    }
    /**
     * Renders the component.
     *
     * @return {TemplateResult|undefined} The template result or undefined if cta is not set.
     */
    render() {
        // Don't render if cta is not provided
        if (!this.cta) {
            return;
        }
        return html `
			<uo-button 
				color=${this.cta.type}
				?disabled=${this.loading || this.cta.disabled}
				?loading=${this.loading}
				@click=${this.handleAddonPluginAction}
			>
				${this.cta.icon ? html `<uo-icon id="${this.cta.icon}"></uo-icon>` : ''}
				${this.cta.label}
			</uo-button>
		`;
    }
    /**
     * Handles the button click event. Initiates the specified addon action (install, activate, update) via a WordPress REST API call. Manages the component's loading state and provides user feedback through snackbars.
     * May trigger a dialog display if the API response requires user intervention.
     *
     * @return {Promise<void>} A promise that resolves when the action handling is complete.
     */
    async handleAddonPluginAction() {
        // Ensure cta and action are defined before proceeding
        if (!this.cta || !this.cta.action || !this.addonID) {
            console.error('Addon action cannot proceed: Missing cta, cta.action, or addonID.');
            return;
        }
        // Set the loading state
        this.loading = true;
        // Define the messages for the snackbar
        const actionInProgressMessages = {
            install: escapeHTML(_x('Installing addon', 'Addons', 'uncanny-automator')),
            activate: escapeHTML(_x('Activating addon', 'Addons', 'uncanny-automator')),
            update: escapeHTML(_x('Updating addon', 'Addons', 'uncanny-automator')),
            download: escapeHTML(_x('Downloading addon', 'Addons', 'uncanny-automator')),
        };
        const actionSuccessMessages = {
            install: escapeHTML(_x('Addon installed', 'Addons', 'uncanny-automator')),
            activate: escapeHTML(_x('Addon activated', 'Addons', 'uncanny-automator')),
            update: escapeHTML(_x('Addon updated', 'Addons', 'uncanny-automator')),
            download: escapeHTML(_x('Addon downloaded', 'Addons', 'uncanny-automator')),
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const $snackbar = createSnackbar({
            message: actionInProgressMessages[this.cta.action],
            iconID: 'spinner-third',
            iconAnimation: 'spin'
        });
        try {
            const response = await apiFetch({
                url: `${UncannyAutomatorBackend.rest.base}automator/v1/addons/plugin-manager/${this.cta.action}/${this.addonID}`,
                method: 'POST',
            });
            // Check if the user needs to take action
            if (!response.success) {
                // If the user intervention data is present, create a dialog
                if (response.user_intervention) {
                    this.createDialog(response.user_intervention);
                }
                else {
                    // Otherwise, show a generic error snackbar for failure without intervention details
                    this.showErrorSnackbar();
                }
                // No further processing needed on failure
                return;
            }
            // --- At this point, we have a successful response ---
            // Celebrate the success
            throwConfetti();
            // Check if we have a direct download URL
            if (response.direct_download) {
                // Open the download URL in a new tab
                window.open(response.direct_download, '_blank');
                return;
            }
            // --- At this point, this is only for install, activate, and update actions ---
            // Update the addon status if data is provided in the response
            if (response.data) {
                this.requestDataUpdate(response.data.addon);
            }
            else {
                // Optionally request a full refresh if specific data isn't returned
                console.warn('Addon action successful, but no updated data received.');
            }
            // Update Snackbar message to success
            createSnackbar({
                message: actionSuccessMessages[this.cta.action],
                iconID: 'check',
                duration: 3000
            });
        }
        catch (error) { // eslint-disable-line @typescript-eslint/no-explicit-any
            // Show error snackbar
            this.showErrorSnackbar(error?.message);
        }
        finally {
            // This block executes whether the try block succeeded or failed
            // Set the loading state to false
            this.loading = false;
            // Remove the "in progress" snackbar if it still exists
            // Check if remove method exists before calling
            if ($snackbar && typeof $snackbar.remove === 'function') {
                $snackbar.remove();
            }
        }
    }
    /**
     * Shows an error snackbar with a generic or specific message.
     * @param {string} [message] Optional specific error message.
     */
    showErrorSnackbar(message) {
        createSnackbar({
            message: message || escapeHTML(__('Something went wrong. Please try again.', 'uncanny-automator')),
            color: 'error',
            iconID: 'xmark',
            duration: 5000
        });
    }
    /**
     * Dispatches a custom event (`automator-page-addons-group-item-data-changed`)
     * to notify parent components that the addon data associated with this button
     * may have changed as a result of a successful action.
     *
     * @param {Addon} data - The updated addon data payload received from the API response.
     */
    requestDataUpdate(data) {
        this.dispatchEvent(new CustomEvent('automator-page-addons-group-item-data-changed', {
            bubbles: true,
            composed: true,
            detail: {
                addon: data
            },
        }));
    }
    /**
     * Creates and renders a `<uo-dialog>` modal when the API response indicates that further user intervention is required (e.g., external authentication).
     * The dialog is appended directly to the document body to avoid z-index issues.
     *
     * @param {AddonUserInterventionData} userIntervention - Data object containing the necessary information for the dialog.
     */
    createDialog(userIntervention) {
        // Create the node that we'll use to render the dialog
        // This node will be added as a direct child of the body node.
        const $wrapper = document.createElement('div');
        document.body.append($wrapper);
        /**
         * Ensure dialog is removed from DOM when closed to prevent memory leaks
         */
        const handleDialogClose = () => {
            $wrapper.remove();
        };
        render(html `
				<uo-dialog
					open
					heading=${escapeHTML(_x('Unable to process request', 'Addons', 'uncanny-automator'))}
					@close=${handleDialogClose}
				>
					<uo-markdown
						allowed-tags="strong,em"
						.content=${userIntervention.message}
					></uo-markdown>

					<uo-button
						slot="primary-action"
						href=${userIntervention.cta.url}
						dialog-action="close"
					>
						${userIntervention.cta.icon ? html `<uo-icon id="${userIntervention.cta.icon}"></uo-icon>` : ''}
						${userIntervention.cta.label}
					</uo-button>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"
					>
						${escapeHTML(_x('Cancel', 'Verb', 'uncanny-automator'))}
					</uo-button>
				</uo-dialog>
			`, $wrapper);
    }
};
__decorate([
    property({ type: String, attribute: 'addon-id' })
], AutomatorPageAddonsGroupItemButtonAction.prototype, "addonID", void 0);
__decorate([
    property({ type: Object })
], AutomatorPageAddonsGroupItemButtonAction.prototype, "cta", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], AutomatorPageAddonsGroupItemButtonAction.prototype, "loading", void 0);
AutomatorPageAddonsGroupItemButtonAction = __decorate([
    customElement('uap-page-addons-group-item-button-action')
], AutomatorPageAddonsGroupItemButtonAction);
export { AutomatorPageAddonsGroupItemButtonAction };
